import { mapPayerNameToCodeForDropdown } from "constants/practiceType";
import { UserRole } from "constants/userRole";
import { useUserWithAccount } from "context/UserAccountProvider";
import { useIsBenefitEngineAdmin } from "hooks/useBenefitEngineAdmin";
import { Label } from "layout/typography/Label";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { Message } from "primereact/message";
import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import styled from "styled-components";

import { BenefitSelectionCard } from "../BenefitSelection/BenefitSelectionCard";
import { BenefitSelectionRulesField } from "./BenefitSelectionRulesField";

const StyledMessage = styled(Message)`
  justify-content: flex-start;
`;

/**
 * CLE-2661 — the account's default pVerify Service Type.
 *
 * This is the question we ask the payer, not a display preference: the practice type selects which
 * benefit blocks come back at all, so it decides which care setting the copay and co-insurance on
 * every new estimate are read from. An account that bills as a surgery center and asks the broad
 * combined question can be quoted an office copay.
 *
 * The empty option stores null, which tracks whatever the global default is rather than freezing
 * today's code into the row. It used to repeat that code's full label, which made it read as a
 * duplicate of the explicit "All Other Medical" entry directly beneath it — the two do behave
 * identically today, and the only difference is what happens if the global default ever changes.
 * Named for that behaviour now instead of restating the code.
 */
const practiceTypeOptions = [
  { name: "Not set — follow the system default", value: "" },
  ...mapPayerNameToCodeForDropdown,
];

/**
 * CLE-2661 — which benefit block copay / co-insurance / OOP are read from FIRST.
 *
 * Stored as `Account.serviceCategory`. Labelled by what it does rather than by the enum name: "Clinic"
 * meant nothing to the people who have to set it, and leaving it unset is what makes a narrow practice
 * type dangerous — asking pVerify for Specialist Office while the chain still starts at Hospital
 * Outpatient finds no block and stores a $0 copay.
 *
 * "Not set" is deliberately NOT described as equivalent to Hospital, though both start at Hospital
 * Outpatient. The unset branch carries an extra guard the explicit ones do not: if no SUMMARY block
 * reports a co-insurance it returns null immediately, without consulting the ServiceDetails lines the
 * named branches read first. So an unset account can come back empty where the same response would
 * have produced a value under an explicit setting — which is the argument for setting it.
 *
 * `Lincoln` is a legacy per-account rule set. It is listed, and marked legacy, so that Lincoln's own
 * account round-trips its stored value instead of being silently cleared on the next save — a
 * dropdown cannot display a value that is not among its options. Nothing else should be set to it.
 *
 * CLE-2675 — the by-payer option is the only one whose first block is not fixed: Medicare Advantage
 * reads Specialist Office (where MA puts its flat per-visit copay), everything else reads Physician
 * Outpatient (pVerify's `Surgical`, the block that carries the treatment percentage). It is a
 * separate option rather than a change to "Specialist Office first" so that the clinics correctly
 * reading office benefits today are not moved. Pair it with the "Specialist Office + Physician
 * Outpatient" Service Type above — the narrow "Specialist - Office" code does not return the
 * Surgical block at all, which leaves a commercial payer with nothing to read.
 */
const benefitPriorityOptions = [
  { name: "Not set — general fallback (less reliable)", value: "" },
  { name: "ASC Facility first (surgery centers)", value: "SurgeryCenter" },
  { name: "Hospital Outpatient first (hospitals)", value: "Hospital" },
  {
    name: "Specialist Office first (clinics, in-office specialists)",
    value: "Clinic",
  },
  {
    name: "By payer — Specialist Office for Medicare Advantage, Surgical for the rest",
    value: "OfficeOrSurgicalByPayerType",
  },
  { name: "Lincoln custom rules (legacy)", value: "Lincoln" },
];

/**
 * CLE-2683 — everything that decides what an eligibility check asks for and which of the answers
 * is used, in one place.
 *
 * These settings were spread through the Feature Flags panel, where the two switches that suppress
 * a whole class of benefit sat several screens away from the dropdowns deciding which benefit gets
 * read. They are one subject and they are now one section.
 */
export function Eligibility() {
  const [expanded, setExpanded] = useState(false);
  const { control } = useFormContext<any>();
  const { userInfo } = useUserWithAccount();
  const isSuperAdmin = userInfo?.scope === UserRole.SUPER_ADMIN;
  const isEngineAdmin = useIsBenefitEngineAdmin();

  return (
    <div className="block bg-white border-round-xl border-1 border-gray-300 p-4">
      <button
        type="button"
        className="flex align-items-center justify-content-between w-full border-none bg-transparent p-0 cursor-pointer text-left"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <span className="text-black font-bold text-lg m-0">Eligibility</span>
        <i
          className={`pi pi-chevron-down text-gray-600 transition-duration-200 transition-all ${
            expanded ? "rotate-180" : ""
          }`}
          style={{ fontSize: "0.75rem" }}
          aria-hidden
        />
      </button>

      {expanded && (
        <div className="flex flex-column gap-4 mt-4">
          <div className="flex gap-3">
            <Controller
              name="eligibilityEnabled"
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputSwitch
                  inputId="eligibilityEnabled"
                  checked={value}
                  onChange={(e) => onChange(e.value)}
                />
              )}
            />
            <div className="flex flex-column">
              <span className="font-medium text-sm mb-1">Eligibility</span>
              <span className="text-sm text-gray-500">
                Enable eligibility checks
              </span>
            </div>
          </div>

          <div className="flex flex-column gap-4 border-bottom-1 border-gray-200 pb-4">
            <span className="font-medium text-sm text-gray-600">
              ELIGIBILITY BENEFITS RULES
            </span>
            <div className="flex gap-3">
              <Controller
                name="facilityRules"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <InputSwitch
                    inputId="facilityRules"
                    checked={value}
                    onChange={(e) => onChange(e.value)}
                  />
                )}
              />
              <div className="flex flex-column">
                <span className="font-medium text-sm mb-1">Facility</span>
                <span className="text-sm text-gray-500">
                  Enable facility rules
                </span>
              </div>
            </div>
            <div className="flex gap-3">
              <Controller
                name="nonFacilityRules"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <InputSwitch
                    inputId="nonFacilityRules"
                    checked={value}
                    onChange={(e) => onChange(e.value)}
                  />
                )}
              />
              <div className="flex flex-column">
                <span className="font-medium text-sm mb-1">Non-Facility</span>
                <span className="text-sm text-gray-500">
                  Enable non-facility rules
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-column gap-2" style={{ maxWidth: "520px" }}>
            <Label htmlFor="defaultPracticeTypeCode">
              Default Service Type (pVerify practice type)
            </Label>

            <Controller
              control={control}
              name="defaultPracticeTypeCode"
              render={({ field: { onChange, value } }) => (
                <Dropdown
                  id="defaultPracticeTypeCode"
                  data-testid="defaultPracticeTypeCode"
                  optionLabel="name"
                  options={practiceTypeOptions}
                  value={value ?? ""}
                  onChange={(e) => onChange(e.value === "" ? null : e.value)}
                />
              )}
            />

            <StyledMessage
              severity="info"
              text="Applies to new estimates for this account. Estimates already sent keep the service type they were originally run with."
            />
          </div>

          <div className="flex flex-column gap-2" style={{ maxWidth: "520px" }}>
            <Label htmlFor="serviceCategory">Benefit Priority</Label>

            <Controller
              control={control}
              name="serviceCategory"
              render={({ field: { onChange, value } }) => (
                <Dropdown
                  id="serviceCategory"
                  data-testid="serviceCategory"
                  optionLabel="name"
                  options={benefitPriorityOptions}
                  value={value ?? ""}
                  onChange={(e) => onChange(e.value === "" ? null : e.value)}
                />
              )}
            />

            <StyledMessage
              severity="info"
              text="Which benefit we read first when the payer returns more than one. Set this to match how the account bills — if it does not match the Service Type above, the estimate can come back with no copay at all. Accounts left unset fall back to a looser rule that is more likely to come back empty."
            />
          </div>

          {/*
            CLE-2792 — the benefit selection card sits BELOW the existing controls and does not
            replace any of them. The Benefit Priority dropdown above still drives the seeder and the
            legacy path; this card configures the engine's own map, and the payer filters render
            inside it as one of its steps.

            🚨 EXACTLY ONE OF THESE TWO RENDERS, AND THE OTHER BRANCH IS THE UNTOUCHED PAGE. The card
            is shown to the one person running the engine cutover (see `useBenefitEngineAdmin`);
            everybody else gets the payer-filters editor where it has always been, saved by "Update
            Hospital" as it has always been. Showing neither would have deleted Ani's only editor on
            deploy; showing both would have given the same rows two savers.

            ⚠️ The gate is not a permission: the API still enforces SuperAdmin behind both.
          */}
          {isEngineAdmin ? (
            <div className="border-top-1 border-gray-200 pt-4">
              <BenefitSelectionCard canEdit={isSuperAdmin} />
            </div>
          ) : (
            <BenefitSelectionRulesField />
          )}
        </div>
      )}
    </div>
  );
}
